import { Box } from "@mui/material";
import { GridApi, GridRowId } from "@mui/x-data-grid-premium";
import { gql } from "generated-graphql";
import { ListOrganizationsQuery, ListOrganizationsQueryVariables } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useRef, useState } from "react";

import { OmnisearchDataGrid } from "./OmnisearchDataGrid";
import CopyableTypography from "./Typography/CopyableTypography";

export const LIST_ORGANIZATIONS = gql(/* GraphQL */ `
  query ListOrganizations(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenants(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...tenant
      }
      count
    }
  }
`);

export type Tenant = NonNullable<NonNullable<ListOrganizationsQuery["tenants"]["items"]>[number]>;

type TenantPickerProps = {
  onTenantSelected: (tenant: Tenant) => void;
};

export function TenantPicker({ onTenantSelected }: TenantPickerProps) {
  const apiRef = useRef<GridApi | null>(null);
  const dataGridRef = useRef<HTMLDivElement | null>(null);
  const [activeId, setActiveId] = useState<GridRowId | null>(null);
  const { isOriginalUserStaff } = useCurrentUser();
  const columns = useMemo(() => {
    const defaultColumns: OmnisearchGridColDef<Tenant>[] = [
      {
        field: "name",
        headerName: "Organization Name",
        flex: 1,
      },
    ];
    if (isOriginalUserStaff) {
      const additionalColumns: OmnisearchGridColDef<Tenant>[] = [
        {
          field: "id",
          headerName: "ID",
          flex: 1.25,
          renderCell: ({ row }) => (
            <CopyableTypography text={row.id} sx={{ fontFamily: "monospace", fontSize: "1em" }} />
          ),
        },
        {
          field: "isDemo",
          headerName: "Demo",
          filterKeyType: "boolean",
          flex: 0.25,
        },
        {
          field: "isDisabled",
          headerName: "Disabled",
          filterKeyType: "boolean",
          flex: 0.25,
        },
      ];
      defaultColumns.push(...additionalColumns);
    }
    return defaultColumns;
  }, [isOriginalUserStaff]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const api = apiRef.current;
      if (!api) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
        return;
      }
      // Keep focus in the search input; just manage highlight/selection
      e.preventDefault();

      const ids = api.getSortedRowIds();
      if (!ids || ids.length === 0) return;

      if (e.key === "Enter") {
        const idForEnter = activeId ?? ids[0];
        const row: Tenant | null = idForEnter != null ? api.getRow(idForEnter) : null;
        if (row) onTenantSelected(row);
        return;
      }

      const currentIdx = activeId != null ? ids.findIndex((id) => id === activeId) : 0;
      const safeIdx = currentIdx >= 0 ? currentIdx : 0;
      const nextIdx =
        e.key === "ArrowDown"
          ? (safeIdx + 1) % ids.length
          : (safeIdx - 1 + ids.length) % ids.length;
      const nextId = ids[nextIdx];
      setActiveId(nextId);
      api.scrollToIndexes({ rowIndex: nextIdx });
    },
    [activeId, onTenantSelected]
  );

  return (
    <Box sx={{ pt: 1 }} onKeyDown={handleKeyDown}>
      <OmnisearchDataGrid<ListOrganizationsQuery, ListOrganizationsQueryVariables, Tenant>
        persistenceKey="tenant-picker"
        autoFocus
        sx={{ height: 510 }}
        withPadding={false}
        apiRef={apiRef}
        dataGridRef={dataGridRef}
        dataQuery={LIST_ORGANIZATIONS}
        fetchPolicy="cache-first"
        columns={columns}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        getCount={(data) => data.tenants.count}
        getItems={(data) => data.tenants.items}
        selectedIds={activeId != null ? [activeId] : []}
        isURLDriven={false}
        onRowClick={({ row }) => {
          onTenantSelected(row);
        }}
        showManageColumns={false}
        onQueryStateChange={({ loading }) => {
          if (loading) return;
          const api = apiRef.current;
          if (!api) return;
          const ids = api.getSortedRowIds();
          if (!ids || ids.length === 0) return;
          // Initialize highlight only if none or if previous selection is gone
          if (activeId == null || !ids.includes(activeId)) {
            setActiveId(ids[0]);
            // Ensure the first row is visible; do not steal focus from the input
            setTimeout(() => {
              api.scrollToIndexes({ rowIndex: 0 });
            }, 0);
          }
        }}
      />
    </Box>
  );
}
